import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Plus, Sparkles, Clock, Users, CalendarPlus } from "lucide-react";
import DatePickerField from "@/components/DatePickerField";
import { fmtDate, fmtTime } from "@/lib/format";
import TimeInput12 from "@/components/TimeInput12";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

export default function AuditionSlotsDialog({ open, onOpenChange, isAdmin }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState({ date: "", end_date: "", start_time: "10:00", end_time: "12:00", slot_minutes: 15, capacity: 1 });
  const [single, setSingle] = useState({ date: "", start_time: "10:00", end_time: "10:15", capacity: 1 });
  const [selected, setSelected] = useState(() => new Set());

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleDate = (dateKey, allIds) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const everySelected = allIds.every((id) => next.has(id));
      allIds.forEach((id) => everySelected ? next.delete(id) : next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audition-slots");
      setSlots(data);
    } catch { toast.error("Could not load slots"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const grouped = useMemo(() => {
    const g = {};
    slots.forEach((s) => { (g[s.date] ||= []).push(s); });
    for (const k of Object.keys(g)) g[k].sort((a, b) => a.start_time.localeCompare(b.start_time));
    return g;
  }, [slots]);

  const dates = useMemo(() => Object.keys(grouped).sort(), [grouped]);
  const totalFilled = useMemo(() => slots.reduce((n, s) => n + (s.filled || 0), 0), [slots]);
  const totalCapacity = useMemo(() => slots.reduce((n, s) => n + (s.capacity || 0), 0), [slots]);

  const generate = async () => {
    if (!session.date) return toast.error("Pick a start date");
    if (!session.start_time || !session.end_time) return toast.error("Set start and end time");
    if (session.end_date && session.end_date < session.date) return toast.error("End date must be on or after the start date");
    try {
      const payload = { ...session };
      if (!payload.end_date) delete payload.end_date;
      const { data } = await api.post("/audition-slots/generate", payload);
      const dayCount = data.days || 1;
      toast.success(
        `Generated ${data.created} slot${data.created === 1 ? "" : "s"}${dayCount > 1 ? ` across ${dayCount} days` : ""}`
      );
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not generate"); }
  };

  const addSingle = async () => {
    if (!single.date) return toast.error("Pick a date");
    try {
      await api.post("/audition-slots", single);
      toast.success("Slot added");
      setSingle((s) => ({ ...s, date: s.date }));   // keep date for quick adds
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add slot"); }
  };

  const remove = async (s) => {
    const label = `${fmtDate(s.date)} · ${fmtTime(s.start_time)}`;
    if (s.filled && !confirm(`${label} has ${s.filled} auditioner${s.filled === 1 ? "" : "s"} assigned. Deleting will unassign them (their signup is kept). Continue?`)) return;
    if (!s.filled && !confirm(`Delete slot ${label}?`)) return;
    try {
      await api.delete(`/audition-slots/${s.id}`);
      toast.success("Slot removed");
      await load();
    } catch { toast.error("Could not delete"); }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!confirm(`Delete ${ids.length} slot${ids.length === 1 ? "" : "s"}? Anyone booked will be unassigned (their signup is kept).`)) return;
    try {
      const { data } = await api.post("/audition-slots/bulk-delete", { slot_ids: ids });
      toast.success(`Removed ${data.deleted} slot${data.deleted === 1 ? "" : "s"}`);
      clearSelection();
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk delete failed"); }
  };

  const bumpCapacity = async (s, delta) => {
    const next = Math.max(1, (s.capacity || 1) + delta);
    try {
      await api.patch(`/audition-slots/${s.id}`, { capacity: next });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="slots-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Clock className="w-5 h-5 text-gold" /> Audition schedule
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">Create and manage the time slots auditioners can book from the public signup form.</DialogDescription>
          <div className="flex items-center gap-3 pt-1 text-[11px] text-zinc-500">
            <span>{slots.length} slots</span>
            <span>·</span>
            <span>{totalFilled}/{totalCapacity} filled</span>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 pr-1 -mr-1">
          {/* Generator (admin only) */}
          {isAdmin && (
            <section className="rounded-lg border border-gold/30 bg-gold/5 p-4" data-testid="generate-session">
              <div className="text-[10px] uppercase tracking-widest text-gold flex items-center gap-1"><Sparkles className="w-3 h-3" /> Generate a session</div>
              <p className="text-[11px] text-zinc-500 mt-1">Create every slot inside a time window at once.</p>
              {/* Row 1: date range — start required, end optional. When both are set we spin up the same daily window on every date in the range. */}
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-[10px]">Start date *</Label>
                  <DatePickerField testid="gen-date" size="sm" value={session.date} onChange={(v) => setSession({ ...session, date: v })} placeholder="Pick date" />
                </div>
                <div>
                  <Label className="text-[10px]">Repeat until <span className="text-zinc-600">(optional)</span></Label>
                  <DatePickerField testid="gen-end-date" size="sm" value={session.end_date} onChange={(v) => setSession({ ...session, end_date: v })} placeholder="Same day only" />
                </div>
              </div>
              {/* Row 2: three side-by-side time / capacity fields — each gets a full column so AM/PM never squishes */}
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div><Label className="text-[10px]">Start</Label><TimeInput12 value={session.start_time} onChange={(v) => setSession({ ...session, start_time: v })} testid="gen-start" /></div>
                <div><Label className="text-[10px]">End</Label><TimeInput12 value={session.end_time} onChange={(v) => setSession({ ...session, end_time: v })} testid="gen-end" /></div>
                <div><Label className="text-[10px]">Per slot</Label><Input type="number" min={1} value={session.capacity} onChange={(e) => setSession({ ...session, capacity: Number(e.target.value) || 1 })} className="bg-[#0A0A0A] border-white/10 h-9" data-testid="gen-capacity" /></div>
              </div>
              {/* Row 3: duration chips + generate button */}
              <div className="mt-3">
                <Label className="text-[10px]">Slot duration (min)</Label>
                <div className="flex gap-1 flex-wrap mt-1">
                  {[5, 10, 15, 20, 30, 45, 60].map((n) => (
                    <button key={n} type="button" onClick={() => setSession({ ...session, slot_minutes: n })} data-testid={`gen-dur-${n}`}
                      className={`h-8 px-2.5 rounded text-xs ${session.slot_minutes === n ? "bg-gold text-black font-semibold" : "border border-white/10 text-zinc-300 hover:bg-white/5"}`}>
                      {n}m
                    </button>
                  ))}
                </div>
              </div>
              <button data-testid="gen-slots-btn" onClick={generate} style={goldBtn} className="mt-3 w-full h-10 rounded font-semibold text-sm inline-flex items-center justify-center gap-1.5 hover:brightness-110">
                <CalendarPlus className="w-4 h-4" /> Generate slots
              </button>
            </section>
          )}

          {/* Bulk-delete toolbar (admin only, shows when any slots selected) */}
          {isAdmin && selected.size > 0 && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 flex items-center justify-between" data-testid="bulk-delete-bar">
              <div className="text-sm text-red-200">
                <strong>{selected.size}</strong> slot{selected.size === 1 ? "" : "s"} selected
              </div>
              <div className="flex items-center gap-2">
                <button onClick={clearSelection} data-testid="bulk-clear-btn" className="h-8 px-2 rounded border border-white/15 text-zinc-300 hover:bg-white/5 text-xs">Clear</button>
                <button onClick={bulkDelete} data-testid="bulk-delete-btn" className="h-8 px-3 rounded border border-red-500/40 bg-red-500/20 text-red-100 hover:bg-red-500/30 text-xs inline-flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Delete selected</button>
              </div>
            </div>
          )}

          {/* Single slot add */}
          {isAdmin && (
            <section className="rounded-lg border border-white/10 p-4 bg-[#0F0F0F]" data-testid="add-single-slot">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Add a single slot</div>
              <div className="grid grid-cols-6 gap-2 mt-3">
                <div className="col-span-2"><DatePickerField testid="single-date" size="sm" value={single.date} onChange={(v) => setSingle({ ...single, date: v })} placeholder="Date" /></div>
                <TimeInput12 value={single.start_time} onChange={(v) => setSingle({ ...single, start_time: v })} testid="single-start" />
                <TimeInput12 value={single.end_time} onChange={(v) => setSingle({ ...single, end_time: v })} testid="single-end" />
                <Input type="number" min={1} value={single.capacity} onChange={(e) => setSingle({ ...single, capacity: Number(e.target.value) || 1 })} className="bg-[#0A0A0A] border-white/10 h-9" data-testid="single-capacity" />
                <button data-testid="add-single-btn" onClick={addSingle} className="h-9 rounded border border-gold/40 text-gold text-sm hover:bg-gold/10 inline-flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Add</button>
              </div>
            </section>
          )}

          {/* Slots list grouped by date */}
          <section>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Current schedule</div>
            {loading ? (
              <div className="text-sm text-zinc-500">Loading…</div>
            ) : dates.length === 0 ? (
              <div className="rounded border border-dashed border-white/10 p-8 text-center bg-[#0F0F0F]">
                <Clock className="w-8 h-8 mx-auto text-zinc-600" />
                <div className="mt-2 font-display text-lg">No slots yet</div>
                <div className="text-sm text-zinc-500 mt-1">Generate a session above to build your audition schedule.</div>
              </div>
            ) : (
              <div className="space-y-3">
                {dates.map((d) => {
                  const dayIds = grouped[d].map((s) => s.id);
                  const allChecked = dayIds.every((id) => selected.has(id));
                  const someChecked = !allChecked && dayIds.some((id) => selected.has(id));
                  return (
                  <div key={d} className="rounded-lg border border-white/10 overflow-hidden">
                    <div className="px-3 py-2 bg-[#0A0A0A] text-[10px] uppercase tracking-widest text-zinc-400 border-b border-white/10 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {isAdmin && (
                          <input
                            type="checkbox"
                            data-testid={`select-day-${d}`}
                            checked={allChecked}
                            ref={(el) => { if (el) el.indeterminate = someChecked; }}
                            onChange={() => toggleDate(d, dayIds)}
                            className="accent-gold w-3.5 h-3.5"
                            title="Select every slot on this date"
                          />
                        )}
                        <span>{fmtDate(d)}</span>
                      </div>
                      <span className="text-zinc-500">{grouped[d].reduce((n, s) => n + s.filled, 0)} / {grouped[d].reduce((n, s) => n + s.capacity, 0)} filled</span>
                    </div>
                    <ul className="divide-y divide-white/5">
                      {grouped[d].map((s) => {
                        const full = s.filled >= s.capacity;
                        return (
                          <li key={s.id} data-testid={`slot-row-${s.id}`} className={`px-3 py-2 flex items-center gap-3 text-sm ${selected.has(s.id) ? "bg-red-500/10" : ""}`}>
                            {isAdmin && (
                              <input
                                type="checkbox"
                                data-testid={`select-slot-${s.id}`}
                                checked={selected.has(s.id)}
                                onChange={() => toggleSelected(s.id)}
                                className="accent-gold w-3.5 h-3.5"
                              />
                            )}
                            <div className="font-mono text-gold w-28 shrink-0">{fmtTime(s.start_time)}–{fmtTime(s.end_time)}</div>
                            <div className="flex-1 min-w-0">
                              {s.filled > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {s.auditioners.map((a) => (
                                    <span key={a.id} className="text-[11px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-200 border border-white/10 truncate max-w-[12rem]" title={a.email}>{a.name}</span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-[11px] text-zinc-500 italic">— empty —</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-[11px]">
                              <span className={`inline-flex items-center gap-1 ${full ? "text-amber-400" : "text-zinc-400"}`}><Users className="w-3 h-3" /> {s.filled}/{s.capacity}</span>
                              {isAdmin && (
                                <>
                                  <button onClick={() => bumpCapacity(s, -1)} data-testid={`cap-down-${s.id}`} className="w-6 h-6 rounded border border-white/10 text-zinc-400 hover:text-white">–</button>
                                  <button onClick={() => bumpCapacity(s, 1)} data-testid={`cap-up-${s.id}`} className="w-6 h-6 rounded border border-white/10 text-zinc-400 hover:text-white">+</button>
                                  <button data-testid={`del-slot-${s.id}`} onClick={() => remove(s)} className="ml-1 w-7 h-7 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10 grid place-items-center"><Trash2 className="w-3.5 h-3.5" /></button>
                                </>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
